/**
 * Theme state. Three states, not two: `system` means no attribute at all, so
 * the CSS media query decides -- and keeps deciding, live, if the OS setting
 * changes while the app is open.
 */
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

export type Theme = 'light' | 'dark' | 'system'
const KEY = 'explorer.theme'

type ThemeContext = {
  theme: Theme
  /** What is actually being shown, with `system` resolved. */
  resolved: 'light' | 'dark'
  setTheme: (t: Theme) => void
  cycle: () => void
}

const Ctx = createContext<ThemeContext | null>(null)

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {}
  return 'system'
}

const prefersDark = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(read)
  const [systemDark, setSystemDark] = useState(prefersDark)

  // `system` follows the OS live, rather than only at boot.
  useEffect(() => {
    if (typeof matchMedia !== 'function') return
    const mq = matchMedia('(prefers-color-scheme: dark)')
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'system') delete root.dataset.theme
    else root.dataset.theme = theme
    try { localStorage.setItem(KEY, theme) } catch {}
  }, [theme])

  const setTheme = useCallback((t: Theme) => setThemeState(t), [])
  const cycle = useCallback(
    () => setThemeState((t) => (t === 'light' ? 'dark' : t === 'dark' ? 'system' : 'light')),
    [],
  )

  const value = useMemo<ThemeContext>(
    () => ({
      theme,
      resolved: theme === 'system' ? (systemDark ? 'dark' : 'light') : theme,
      setTheme,
      cycle,
    }),
    [theme, systemDark, setTheme, cycle],
  )

  return <Ctx value={value}>{children}</Ctx>
}

export function useTheme(): ThemeContext {
  const c = use(Ctx)
  if (!c) throw new Error('useTheme outside ThemeProvider')
  return c
}

// ---------------------------------------------------------------------
// Density
// ---------------------------------------------------------------------

/**
 * Reading size and control size, as a preference.
 *
 * Same three-state shape as the theme, for the same reason: `auto` means no
 * attribute at all, so `(pointer: coarse)` in tokens.css decides -- a phone
 * gets the comfortable scale without anyone choosing it, and a mouse keeps
 * the density the app was designed around.
 *
 * The two explicit choices exist because the automatic answer is wrong at
 * both ends: a 27-inch display at 100% scale wants `comfortable` with a
 * mouse attached, and someone reading a 400-row statement on a tablet may
 * genuinely want `compact`.
 */
export type Density = 'auto' | 'comfortable' | 'compact'
const DENSITY_KEY = 'explorer.density'

type DensityContext = {
  density: Density
  setDensity: (d: Density) => void
}

const DensityCtx = createContext<DensityContext | null>(null)

function readDensity(): Density {
  try {
    const v = localStorage.getItem(DENSITY_KEY)
    if (v === 'auto' || v === 'comfortable' || v === 'compact') return v
  } catch {}
  return 'auto'
}

export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(readDensity)

  useEffect(() => {
    const root = document.documentElement
    if (density === 'auto') delete root.dataset.density
    else root.dataset.density = density
    try { localStorage.setItem(DENSITY_KEY, density) } catch {}
  }, [density])

  const setDensity = useCallback((d: Density) => setDensityState(d), [])
  const value = useMemo<DensityContext>(() => ({ density, setDensity }), [density, setDensity])

  return <DensityCtx value={value}>{children}</DensityCtx>
}

export function useDensity(): DensityContext {
  const c = use(DensityCtx)
  if (!c) throw new Error('useDensity outside DensityProvider')
  return c
}
